import { HttpEventType, HttpResponse } from '@angular/common/http'
import { Component, EventEmitter, forwardRef, HostListener, Inject, Input, OnInit, Output, PLATFORM_ID, } from '@angular/core'
import { Subscription } from 'rxjs'
import { finalize, take } from 'rxjs/operators'
import { TransferHttpService } from 'src/app/services/transfer-http/transfer-http.service'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { isPlatformBrowser } from '@angular/common'

@Component({
  selector: 'aims-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true,
    },
  ],
})
export class FileUploadComponent implements OnInit, ControlValueAccessor {
  _fileName: string
  get fileName() {
    return this._fileName
  }
  // user input to component
  @Input() set fileName(val: string) {
    this._fileName = val
    this.onChange(this._fileName);
  }

  @Input() saveUrl: string
  @Input() removeUrl: string = null
  @Input() downloadUrl: string = null
  @Input() allowedFileType: string = '.bmp,.pdf,.csv,.jpg,.jpeg,.gif,.png'
  @Input() maxFileSize: number = 4194304
  @Input() downloadOnly: boolean = false
  uploadProgress: number = null
  uploadSub: Subscription

  @Output() fileDeleted = new EventEmitter()
  @Output() success = new EventEmitter()     // uploaded and processed
  @Output() error = new EventEmitter()
  @Output() uploaded = new EventEmitter()    // uploaded but not processed

  dragAreaClass: string
  dragAreaLabel: string = 'Drag'

  showTooBig: boolean = false

  //Spinner
  spinnerText: string = 'Loading...'
  showSpinner: boolean = false

  constructor(private transferHttp: TransferHttpService, @Inject(PLATFORM_ID) private platformId: Object) {}

  ngOnInit(): void {
    this.reset()
  }

  isDisabled = false;

  // ControlValueAccessor implementation

  // This method is called by the forms API to write to the view when programmatic changes from model to view are requested.
  writeValue(value: any) {
    if (value !== undefined) {
      this._fileName = value
    }
  }

  // defaults, so your app doesn't crash when one isn't (yet) registered
  onChange = (_: any) => {}
  onTouched = () => {}

  // This method is called by the forms API on initialization to update the form model when values propagate from the view to the model.
  registerOnChange(fn: any) {
    this.onChange = fn
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn
  }

   setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
  // End ControlValueAccessor implementation

  // Host Listener methods
  @HostListener('dragover', ['$event']) onDragOver(event: any) {
    this.dragAreaClass = 'droparea'
    this.dragAreaLabel = 'Drop'
    event.preventDefault()
  }
  @HostListener('dragenter', ['$event']) onDragEnter(event: any) {
    this.dragAreaClass = 'droparea'
    this.dragAreaLabel = 'Drop'
    event.preventDefault()
  }
  @HostListener('dragend', ['$event']) onDragEnd(event: any) {
    this.dragAreaClass = 'dragarea'
    this.dragAreaLabel = 'Drag'
    event.preventDefault()
  }
  @HostListener('dragleave', ['$event']) onDragLeave(event: any) {
    this.dragAreaClass = 'dragarea'
    this.dragAreaLabel = 'Drag'
    event.preventDefault()
  }
  @HostListener('drop', ['$event']) onDrop(event: any) {
    this.dragAreaClass = 'dragarea'
    this.dragAreaLabel = 'Drag'
    event.preventDefault()
    event.stopPropagation()
    if (event.dataTransfer.files) {
      let files: FileList = event.dataTransfer.files
      this.uploadFiles(files)
    }
  }

  private fileTooBig(fileName: string): void {
    console.log(`file ${fileName} too large!`)
    this.showTooBig = true
    setTimeout(() => {
      this.showTooBig = false
    }, 3000)
  }

  public onFileSelected(event): void {
    this.uploadFiles(event.target.files)
  }

  private uploadFiles(files: FileList) {
    if (files.length == 0) {
      console.log('No file selected!')
      return
    }

    let file: File = files[0]
    if (file.size > this.maxFileSize) {
      this.fileTooBig(file.name)
      return
    }
    this.fileName = file.name

    const formData = new FormData()
    formData.append('Files', file)

    const upload$ = this.transferHttp
      .post(this.saveUrl, formData, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(finalize(() => this.reset()))

    this.uploadSub = upload$.subscribe({
      next: (event) => {
        if (event.type == HttpEventType.UploadProgress) {
          this.uploadProgress = Math.round(100 * (event.loaded / event.total))
          if (this.uploadProgress == 100) {
            this.uploaded.emit();
          }
          console.log(`File is ${this.uploadProgress}% loaded.`)
        } else if (event instanceof HttpResponse) {
          this.success.emit(event);
        }
      },
      error: (err) => {
        // console.log('Upload Error:', err)
        this.error.emit(err);
      }
    })
  }

  cancelUpload() {
    this.uploadSub?.unsubscribe()
    this.reset()
  }

  reset() {
    this.uploadProgress = null
    this.uploadSub = null
    this.showSpinner = false
    this.dragAreaClass = 'dragarea'
  }

  fileDelete() {
    this.spinnerText = 'Deleting file...'
    this.showSpinner = true

    this.transferHttp.post(this.removeUrl, null).subscribe((ret) => {
      this.showSpinner = false
      this.fileName = null
      this.fileDeleted.emit()
    })
  }

  downloadFile(clickEvent) {
    if (isPlatformBrowser(this.platformId)) {
      clickEvent.stopPropagation()
      this.transferHttp
        .get(this.downloadUrl, { responseType: 'arraybuffer' })
        .pipe(take(1))
        .subscribe((response) => {
          let dataType = response.type
          let binaryData = []
          binaryData.push(response)
          let downloadLink = document.createElement('a')
          downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }))
          if (this.fileName) downloadLink.setAttribute('download', this.fileName)
          document.body.appendChild(downloadLink)
          downloadLink.click()
          downloadLink.parentNode.removeChild(downloadLink)
        })
    }
  }
}
